import { ALL_CADENCES, CLOCK_CADENCE } from "./clock-cadences";

// A callback function that will be called when the wall clock ticks.
type Listener = () => void;

/**
 * Module-level singleton — shared across the entire app.
 *
 * Stores the last timestamp per cadence in lastTickTimeByCadence and tracks listeners for each cadence.
 */
const lastTickTimeByCadence = new Map<CLOCK_CADENCE, number>(ALL_CADENCES.map((c) => [c, Date.now()]));
const listeners = new Map<CLOCK_CADENCE, Set<Listener>>(ALL_CADENCES.map((c) => [c, new Set()]));

// Should always be exactly 1; used only in dev to catch accidental duplicate providers.
// HMR note: module scope persists across fast-refresh cycles, so this may read > 1
// spuriously after a hot-reload that unmounts and remounts the provider.
let providerMountCount = 0;

function getOrThrow<V>(map: Map<CLOCK_CADENCE, V>, cadence: CLOCK_CADENCE): V {
    const value = map.get(cadence);
    if (value === undefined)
        throw new Error(`WallClockService: unknown cadence ${cadence}. Add it to CLOCK_CADENCE and ALL_CADENCES.`);
    return value;
}

/**
 * Pub/sub for wall clock ticks. Does not schedule timers — WallClockProvider calls `tick` fn.
 */
export const wallClockService = {
    // Advance the clock boundary for this cadence — WallClockProvider calls this on each interval; triggers re-renders in subscribed hooks.
    tick(cadence: CLOCK_CADENCE): void {
        lastTickTimeByCadence.set(cadence, Date.now());
        getOrThrow(listeners, cadence).forEach((listener) => listener());
    },
    // Subscribe to this cadence; returns unsubscribe.
    subscribe(cadence: CLOCK_CADENCE, listener: Listener): () => void {
        const listenerSet = getOrThrow(listeners, cadence);
        listenerSet.add(listener);
        return () => {
            listenerSet.delete(listener);
        };
    },
    // Timestamp of the most recent tick for this cadence (external-store snapshot).
    getTickTime(cadence: CLOCK_CADENCE): number {
        return getOrThrow(lastTickTimeByCadence, cadence);
    },
    // Provider lifecycle — used for dev duplicate-provider warning.
    notifyMounted(): void {
        providerMountCount++;
        if (process.env.NODE_ENV !== "production" && providerMountCount > 1) {
            console.warn(
                `WallClockService: ${providerMountCount} instances detected. Only one service should be mounted at a time.`,
            );
        }
    },
    notifyUnmounted(): void {
        providerMountCount = Math.max(0, providerMountCount - 1);
    },
};
